import React, { useMemo } from "react";
import PropTypes from "prop-types";
import moment from "moment";

import { periodWindowLabelTitle } from "../../helpers/periodWindowLabel";
import { resolveCardWindow } from "../../helpers/cardWindow";
import useProfitTrend, {
  laborModeOf,
  money,
  moneyShort,
  pct,
  resolveLaborWidget,
} from "../ProfitTrend/data";
import { SERIES, seriesColorsOf } from "../ProfitTrend/TrendChart";
import CardBusy from "../CardScope/CardBusy";
import TileTip from "../CustomWidget/TileTip";
import {
  netBonusState,
  netModeOf,
  netRail,
  payAmount,
  payParts,
  payText,
  payUnitOf,
  reqFill,
  resolveProfitWidget,
  sanitizeNetBonus,
  usesAmount,
  usesShare,
} from "./netBonus";
import "./netBonus.css";

/**
 * Net Profit Bonus — hit this net profit, earn this; hit a higher one, earn more.
 *
 * The figure is NOT worked out here. It is read from a Revenue, Expenses & Labor
 * card on the same board — the one this card is pointed at — by running that
 * card's own data hook on that card's own saved settings, for THIS card's
 * window. So the Net profit the ladder is paid on is, to the cent, the Net
 * profit on that card's tile, and revenue, expenses and payroll are only ever
 * set up in one place. Nothing in this file decides arithmetic about money;
 * ./netBonus.js decides where the figure sits on the ladder.
 *
 * Display-only, like every other bonus card: nothing here credits money.
 */

/* Used while no profit card feeds this one. Both sources typed, so the hook has
   nothing to fetch — a card with nothing to read must not go and load a
   company's revenue for a number it is about to hide. Module-level so its
   identity is stable across renders. */
const UNLINKED = { revenueMode: "manual", laborMode: "manual" };

/* The gate palette the board already uses (GATE_STYLE in BeatTheYeti). */
const TONES = {
  ok: { ink: "#12a150", soft: "rgba(18, 161, 80, 0.1)", border: "rgba(18, 161, 80, 0.4)" },
  bad: { ink: "#d92d20", soft: "rgba(240, 68, 56, 0.1)", border: "rgba(240, 68, 56, 0.4)" },
  flat: { ink: null, soft: null, border: null },
};

const Check = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </svg>
);
Check.propTypes = { size: PropTypes.number };
Check.defaultProps = { size: 9 };

const moneyPay = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;

const NetProfitBonus = ({
  settings, title, boardWindow, boardCustom, boardWidgets, primary,
}) => {
  const { window: bucket, custom: resolvedCustom } = resolveCardWindow(settings, {
    window: boardWindow,
    custom: boardCustom,
  });
  const custom = bucket === "custom" ? (resolvedCustom || boardCustom) : null;

  /* Not memoised, for the reason the profit card gives for its own payroll
     link: it is one filter over a handful of cards, and any cheap cache key
     would miss the case that matters — the profit card's OWN settings being
     edited, which changes nothing about its id and everything about the Net. */
  const link = resolveProfitWidget(settings, boardWidgets);
  const profitOptions = link.widget ? (link.widget.options || {}) : null;
  /* The profit card gets its Labor from a Payroll vs Revenue card, so to read
     the same Net this card has to hand the hook that card's options too. */
  const payrollLink = profitOptions && laborModeOf(profitOptions) === "widget"
    ? resolveLaborWidget(profitOptions, boardWidgets)
    : null;
  const laborOptions = payrollLink && payrollLink.widget ? (payrollLink.widget.options || null) : null;

  const data = useProfitTrend(profitOptions || UNLINKED, laborOptions, bucket, custom);

  const bonusKey = JSON.stringify(settings?.netBonus || null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const bonus = useMemo(() => sanitizeNetBonus(settings?.netBonus), [bonusKey]);

  const hide = settings?.hide || {};
  const cardTitle = title || settings?.title || "Net Profit Bonus";
  const periodLabel = data.displayTo && bucket === "month"
    ? `${moment(data.from).format("MMM D")} – ${moment(data.displayTo).format("D")}`
    : periodWindowLabelTitle(bucket, custom);

  /* Each of these is a real dead end, not a slow load — the card would draw a
     confident $0 and look like a finding. */
  const notReady = () => {
    if (link.missing) {
      return {
        head: "No Revenue, Expenses & Labor card on this dashboard",
        body: "This bonus is paid on the Net profit that card works out. Add one to the board, then this card picks it up on its own.",
      };
    }
    if (link.ambiguous) {
      return {
        head: link.stale ? "That profit card is gone" : `${link.choices.length} Revenue, Expenses & Labor cards on this dashboard`,
        body: link.stale
          ? "The card this one was reading has been removed. Pick another in Edit component → Setup."
          : "They can be set up differently, so this card won't guess which Net profit to pay on. Pick one in Edit component → Setup.",
      };
    }
    if (!bonus) {
      return {
        head: "No steps set yet",
        body: "Add the net profit lines and what each one pays in Edit component → Setup.",
      };
    }
    if (payrollLink && (payrollLink.missing || payrollLink.ambiguous)) {
      return {
        head: "Revenue, Expenses & Labor isn't finished",
        body: "Its Labor comes from a Payroll vs Revenue card it can't find yet, so its Net profit isn't complete. Finish it there and this bonus follows.",
      };
    }
    if (data.loading) return { loading: true };
    if (data.revenue <= 0) {
      return {
        head: "No revenue yet",
        body: "Net profit is measured against revenue for the period. Once the Revenue, Expenses & Labor card has revenue, this ladder fills in.",
      };
    }
    return null;
  };
  const blocked = notReady();

  const netColor = seriesColorsOf(profitOptions || {}).net || SERIES.net.color;
  const shell = (children) => (
    <div className={`gmbw-sales-card npb${data.refreshing ? " npb-refreshing" : ""}`}>
      <CardBusy active={!link.missing && (data.loading || data.refreshing)} cover={data.loading} label={data.loading ? "Loading…" : "Updating for date range…"} />
      <div className="gmbw-sales-head">
        <span className="gmbw-sales-title">{cardTitle}</span>
        <span className="gmbw-sales-period">{periodLabel}</span>
      </div>
      <div className="gmbw-sales-body">{children}</div>
    </div>
  );

  if (blocked) {
    if (blocked.loading) return shell(null);
    return shell(
      <div className="npb-empty">
        <b>{blocked.head}</b>
        {blocked.body}
      </div>,
    );
  }

  const { steps } = bonus;
  const mode = netModeOf(bonus);
  const figures = { amount: data.net, share: data.netPct, revenue: data.revenue };
  const st = netBonusState(bonus, figures);
  const rail = netRail(bonus, figures);
  const team = bonus.per !== "player";
  const who = team ? "team pot" : "each player";
  const each = team ? "" : " each";
  const paying = st.pay > 0;
  const netNegative = data.net < 0;
  const inProgress = data.paceElapsed < 1;

  const payTone = paying ? TONES.ok : TONES.flat;
  const netTone = netNegative ? TONES.bad : TONES.flat;
  let stepWord = "Not yet";
  if (st.done) stepWord = "Top step";
  else if (st.idx >= 0) stepWord = `Step ${st.idx + 1} of ${steps.length}`;

  const reqLabel = { amount: "Net profit", share: "Net margin" };
  const reqHave = (r) => (r.key === "amount" ? money(r.have) : pct(r.have));
  const reqNeed = (r) => (r.key === "amount" ? money(r.need) : pct(r.need));
  const reqGap = (r) => (r.key === "amount" ? `${money(r.gap)} to go` : `${pct(r.gap)} to go`);

  /* The step currently paying, and what it is written as — a percent step shows
     its rule beside the dollars it works out to. */
  const reached = st.idx >= 0 ? steps[st.idx] : null;
  const paySub = reached && payUnitOf(reached) === "%"
    ? `${payText(reached)} · ${who}`
    : `${who} · up to ${moneyPay(st.max)}`;

  const tip = {
    eyebrow: `${bonus.label} · ${periodLabel}`,
    rows: [
      { label: "Net profit", value: money(data.net) },
      { label: "Net margin", value: pct(data.netPct) },
      { label: "Step reached", value: st.idx >= 0 ? `${st.idx + 1} of ${steps.length}` : "None yet" },
      ...(reached && payUnitOf(reached) === "%" ? [{ label: "Pays", value: payText(reached) }] : []),
    ],
    verdict: {
      text: paying ? `Paying ${moneyPay(st.pay)}${each}` : "Not paying yet",
      tone: paying ? "good" : "flat",
    },
    note: `${team ? "One pot for the team." : "Every player on the card earns this."} ${inProgress ? "Net profit moves every day — this is what it pays if the period ended today." : "The period is over — this is what it paid."}`,
  };

  return shell(
    <div
      className="npb-wrap"
      style={{ "--npb-accent": primary, "--npb-net": netColor }}
      role="group"
      aria-label={`${bonus.label}: net profit ${money(data.net)}, paying ${moneyPay(st.pay)}${each}`}
    >
      {/* ── The two figures ─────────────────────────────────────────────── */}
      <div className="npb-hero">
        <div
          className="npb-tile npb-net"
          style={netTone.border ? { borderColor: netTone.border } : undefined}
        >
          <span className="npb-k">
            <i className="npb-swatch" style={{ background: netColor }} />
            Net profit
          </span>
          <span className="npb-v">
            <b style={netTone.ink ? { color: netTone.ink } : undefined}>{money(data.net)}</b>
            <span
              className="npb-pill"
              style={netTone.ink ? { color: netTone.ink, background: netTone.soft } : undefined}
            >
              {pct(data.netPct)} margin
            </span>
          </span>
          <span className="npb-sub">of {money(data.revenue)} revenue</span>
        </div>

        <div className="npb-tip">
          <TileTip {...tip}>
            <div
              className={`npb-tile npb-pay${paying ? " on" : ""}${st.done ? " top" : ""}`}
              style={payTone.border ? { borderColor: payTone.border } : undefined}
            >
              <span className="npb-k">{bonus.label}</span>
              <span className="npb-v">
                <b style={payTone.ink ? { color: payTone.ink } : undefined}>{moneyPay(st.pay)}</b>
                <span
                  className="npb-pill"
                  style={payTone.ink ? { color: payTone.ink, background: payTone.soft } : undefined}
                >
                  {stepWord}
                </span>
              </span>
              <span className="npb-sub">{paySub}</span>
            </div>
          </TileTip>
        </div>
      </div>

      {/* ── The ladder ──────────────────────────────────────────────────── */}
      <div className="npb-lad">
        <div className="npb-track" aria-hidden>
          <div className="npb-rail">
            <div className="npb-fill" style={{ width: `${rail.valueX}%` }} />
          </div>
          {rail.marks.map((x, i) => (
            <i
              key={`${steps[i].at ?? ""}-${steps[i].atPct ?? ""}`}
              className={`npb-mark${st.flags[i].hit ? " on" : ""}`}
              /* The top step sits on the very end of the rail. */
              style={{ left: `clamp(5px, ${x}%, calc(100% - 5px))` }}
            />
          ))}
        </div>

        {!hide.steps && (
          <div className="npb-steps" style={{ "--npb-cols": steps.length }}>
            {steps.map((s, i) => {
              const f = st.flags[i];
              let cls = "npb-step";
              if (f.hit) cls += " hit";
              else if (i === st.idx + 1) cls += " next";
              return (
                <div key={`${s.at ?? ""}-${s.atPct ?? ""}`} className={cls}>
                  <span className="npb-st-n">
                    {f.hit && <Check />}
                    Step {i + 1}
                  </span>
                  <span
                    className="npb-st-pay"
                    title={payUnitOf(s) === "%" ? `About ${moneyPay(payAmount(s, data.revenue))} at this period's revenue` : undefined}
                  >
                    {payParts(s).value}
                    {payParts(s).unit && <small>{payParts(s).unit}</small>}
                  </span>
                  <span className="npb-st-req">
                    {usesAmount(mode) && (
                      <span className={mode === "both" && f.amountMet ? "met" : ""}>
                        {mode === "both" && f.amountMet && <Check size={8} />}
                        {moneyShort(s.at)}
                      </span>
                    )}
                    {mode === "both" && <em>&amp;</em>}
                    {usesShare(mode) && (
                      <span className={mode === "both" && f.shareMet ? "met" : ""}>
                        {mode === "both" && f.shareMet && <Check size={8} />}
                        {pct(s.atPct)}
                      </span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── What unlocks the next step ──────────────────────────────────── */}
      {!hide.next && (
        st.done ? (
          <div className="npb-next done">
            <span className="npb-done-i"><Check size={11} /></span>
            <span>
              Top step reached. {team ? "The team pot is" : "Every player is"} at the full {moneyPay(st.pay)}.
            </span>
          </div>
        ) : (
          <div className="npb-next">
            <div className="npb-next-h">
              <span>{st.idx < 0 ? "First step" : "Next step"}</span>
              <b>
                {payUnitOf(st.next) === "%" ? "≈ " : ""}
                {moneyPay(payAmount(st.next, data.revenue))}
                <small>{` ${who}${payUnitOf(st.next) === "%" ? ` · ${payText(st.next)}` : ""}`}</small>
              </b>
            </div>
            {st.rows.map((r) => (
              <div key={r.key} className={`npb-req${r.ok ? " ok" : ""}`}>
                <span className="npb-req-k">
                  {r.ok ? <Check size={10} /> : <i className="npb-ring" />}
                  {reqLabel[r.key]}
                  <em className="npb-req-v">{reqHave(r)} of {reqNeed(r)}</em>
                </span>
                <span className="npb-req-t">{r.ok ? "Met" : reqGap(r)}</span>
                <span className="npb-req-bar"><i style={{ width: `${reqFill(r.have, r.need)}%` }} /></span>
              </div>
            ))}
          </div>
        )
      )}
    </div>,
  );
};

NetProfitBonus.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  settings: PropTypes.object,
  title: PropTypes.string,
  boardWindow: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  boardCustom: PropTypes.object,
  /* Every other card on this board — how the profit card is found. */
  // eslint-disable-next-line react/forbid-prop-types
  boardWidgets: PropTypes.array,
  primary: PropTypes.string,
};

NetProfitBonus.defaultProps = {
  settings: {},
  title: undefined,
  boardWindow: null,
  boardCustom: null,
  boardWidgets: [],
  primary: "#1B598E",
};

export default NetProfitBonus;
